import {
    OPEN_AGENT_POPUP,
    CLOSE_AGENT_POPUP,
    ADD_MESSAGE,
    SET_MESSAGES,
    CLEAR_MESSAGES,
    UPDATE_MESSAGE,
    ADD_CONVERSATION_PAIR,
    SET_CONVERSATION_PAIRS,
    CLEAR_CONVERSATION_PAIRS,
    SET_WORKSPACE_OPTIONS,
    SET_WORKSPACE_OPTIONS_LOADING,
    SET_WORKSPACE_OPTIONS_LOADED,
    SET_CHAT_STEP,
    SET_SELECTED_LOCATIONS,
    SET_FIRST_RECOMMENDATION_DISPLAYED,
    SET_INITIAL_MESSAGE_SET,
    SET_HAS_AUTO_FETCHED,
    SET_LOADING,
    SET_INITIAL_LOADING,
    SET_CONTACT_FORM,
    SET_SHOW_CONTACT_FORM,
    RESET_CHAT_SESSION,
    CLEAR_CHAT_SESSION,
    SET_SPACE_IDS,
    SET_EVENT_IDS,
    ADD_SPACE_ID,
    ADD_EVENT_ID,
    FETCH_USER_DATA_REQUEST,
    FETCH_USER_DATA_SUCCESS,
    FETCH_USER_DATA_FAILURE,
    CLEAR_USER_DATA,
    UPDATE_USER_DATA
} from './agentActionTypes';

const initialState = {
    // Popup visibility
    isPopupOpen: false,

    // Chat state management
    messages: [],
    conversationPairs: [],

    // Workspace options management
    workspaceOptions: [],
    workspaceOptionsLoading: false,
    workspaceOptionsLoaded: false,

    // Session management
    step: 1,
    selectedLocations: [],
    firstRecommendationDisplayed: false,
    initialMessageSet: false,
    hasAutoFetched: false,

    // Loading states
    loading: false,
    initialLoading: false,

    // Contact form management
    contactForm: {
        name: '',
        email: '',
        phoneNumber: '',
        message: ''
    },
    showContactForm: false,

    // Space and event IDs for navigation
    spaceIds: [],
    eventIds: [],

    // Session persistence
    sessionId: null,
    lastActivity: null,

    // User data management
    userData: null, // Will be populated from API with { bookingCount, userName, lastBookedSpace }
    userDataLoading: false,
    userDataError: null
};

const agentReducer = (state = initialState, action) => {
    switch (action.type) {
        case OPEN_AGENT_POPUP:
            return {
                ...state,
                isPopupOpen: true,
                sessionId: state.sessionId || `session_${Date.now()}`,
                lastActivity: new Date().toISOString()
            };

        case CLOSE_AGENT_POPUP:
            return {
                ...state,
                isPopupOpen: false,
                lastActivity: new Date().toISOString()
            };

        // Chat state management
        case ADD_MESSAGE:
            return {
                ...state,
                messages: [...state.messages, action.payload],
                lastActivity: new Date().toISOString()
            };

        case SET_MESSAGES:
            return {
                ...state,
                messages: action.payload,
                lastActivity: new Date().toISOString()
            };

        case CLEAR_MESSAGES:
            return {
                ...state,
                messages: [],
                lastActivity: new Date().toISOString()
            };

        case UPDATE_MESSAGE:
            return {
                ...state,
                messages: state.messages.map(msg =>
                    msg.id === action.payload.messageId
                        ? { ...msg, ...action.payload.updates }
                        : msg
                ),
                lastActivity: new Date().toISOString()
            };

        // Conversation pairs management
        case ADD_CONVERSATION_PAIR:
            return {
                ...state,
                conversationPairs: [...state.conversationPairs, action.payload],
                lastActivity: new Date().toISOString()
            };

        case SET_CONVERSATION_PAIRS:
            return {
                ...state,
                conversationPairs: action.payload,
                lastActivity: new Date().toISOString()
            };

        case CLEAR_CONVERSATION_PAIRS:
            return {
                ...state,
                conversationPairs: [],
                lastActivity: new Date().toISOString()
            };

        // Workspace options management
        case SET_WORKSPACE_OPTIONS:
            return {
                ...state,
                workspaceOptions: action.payload,
                lastActivity: new Date().toISOString()
            };

        case SET_WORKSPACE_OPTIONS_LOADING:
            return {
                ...state,
                workspaceOptionsLoading: action.payload,
                lastActivity: new Date().toISOString()
            };

        case SET_WORKSPACE_OPTIONS_LOADED:
            return {
                ...state,
                workspaceOptionsLoaded: action.payload,
                lastActivity: new Date().toISOString()
            };

        // Session management
        case SET_CHAT_STEP:
            return {
                ...state,
                step: action.payload,
                lastActivity: new Date().toISOString()
            };

        case SET_SELECTED_LOCATIONS:
            return {
                ...state,
                selectedLocations: action.payload,
                lastActivity: new Date().toISOString()
            };

        case SET_FIRST_RECOMMENDATION_DISPLAYED:
            return {
                ...state,
                firstRecommendationDisplayed: action.payload,
                lastActivity: new Date().toISOString()
            };

        case SET_INITIAL_MESSAGE_SET:
            return {
                ...state,
                initialMessageSet: action.payload,
                lastActivity: new Date().toISOString()
            };

        case SET_HAS_AUTO_FETCHED:
            return {
                ...state,
                hasAutoFetched: action.payload,
                lastActivity: new Date().toISOString()
            };

        // Loading states
        case SET_LOADING:
            return {
                ...state,
                loading: action.payload,
                lastActivity: new Date().toISOString()
            };

        case SET_INITIAL_LOADING:
            return {
                ...state,
                initialLoading: action.payload,
                lastActivity: new Date().toISOString()
            };

        // Contact form management
        case SET_CONTACT_FORM:
            return {
                ...state,
                contactForm: { ...state.contactForm, ...action.payload },
                lastActivity: new Date().toISOString()
            };

        case SET_SHOW_CONTACT_FORM:
            return {
                ...state,
                showContactForm: action.payload,
                lastActivity: new Date().toISOString()
            };

        // User data management
        case FETCH_USER_DATA_REQUEST:
            return {
                ...state,
                userDataLoading: true,
                userDataError: null,
                lastActivity: new Date().toISOString()
            };

        case FETCH_USER_DATA_SUCCESS:
            console.log('Chatbot: Reducer storing userData:', action.payload);
            return {
                ...state,
                userData: action.payload,
                userDataLoading: false,
                lastActivity: new Date().toISOString()
            };

        case FETCH_USER_DATA_FAILURE:
            return {
                ...state,
                userDataLoading: false,
                userDataError: action.payload,
                lastActivity: new Date().toISOString()
            };

        case CLEAR_USER_DATA:
            return {
                ...state,
                userData: null,
                lastActivity: new Date().toISOString()
            };

        case UPDATE_USER_DATA:
            return {
                ...state,
                userData: { ...state.userData, ...action.payload },
                lastActivity: new Date().toISOString()
            };

        // Chat session management
        case RESET_CHAT_SESSION:
            return {
                ...state,
                step: 1,
                selectedLocations: [],
                firstRecommendationDisplayed: false,
                initialMessageSet: false,
                hasAutoFetched: false,
                loading: false,
                initialLoading: false,
                showContactForm: false,
                contactForm: {
                    name: '',
                    email: '',
                    phoneNumber: '',
                    message: ''
                },
                lastActivity: new Date().toISOString()
            };

        case CLEAR_CHAT_SESSION:
            return {
                ...state,
                messages: [],
                conversationPairs: [],
                spaceIds: [],
                eventIds: [],
                step: 1,
                selectedLocations: [],
                firstRecommendationDisplayed: false,
                initialMessageSet: false,
                hasAutoFetched: false,
                loading: false,
                initialLoading: false,
                showContactForm: false,
                contactForm: {
                    name: '',
                    email: '',
                    phoneNumber: '',
                    message: ''
                },
                sessionId: `session_${Date.now()}`,
                lastActivity: new Date().toISOString()
            };

        // Space and event IDs management
        case SET_SPACE_IDS:
            return {
                ...state,
                spaceIds: action.payload,
                lastActivity: new Date().toISOString()
            };

        case SET_EVENT_IDS:
            return {
                ...state,
                eventIds: action.payload,
                lastActivity: new Date().toISOString()
            };

        case ADD_SPACE_ID:
            return {
                ...state,
                spaceIds: [...state.spaceIds, action.payload],
                lastActivity: new Date().toISOString()
            };

        case ADD_EVENT_ID:
            return {
                ...state,
                eventIds: [...state.eventIds, action.payload],
                lastActivity: new Date().toISOString()
            };

        default:
            return state;
    }
};

export default agentReducer; 